import React from 'react'
import { styled } from '@mui/material/styles'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Card, CardContent, Grid, Typography } from '@mui/material/'
import Skeleton from '@mui/material/Skeleton'
import { CatalogFragment } from '@services/cloud/client'
import { ImageCarousel } from './ImageCarousel'

const PREFIX = 'CatalogView'

const classes = {
  container: `${PREFIX}-container`,
  containerTop: `${PREFIX}-containerTop`,
  contactButtonContainer: `${PREFIX}-contactButtonContainer`,
  contactButton: `${PREFIX}-contactButton`,
  description: `${PREFIX}-description`,
}

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginTop: theme.spacing(1),
  },

  [`& .${classes.containerTop}`]: {
    marginTop: theme.spacing(2),
  },

  [`& .${classes.contactButtonContainer}`]: {
    marginTop: theme.spacing(3),
  },

  [`& .${classes.contactButton}`]: {
    marginTop: theme.spacing(1),
  },

  [`& .${classes.description}`]: {
    whiteSpace: 'pre-line',
  },
}))

const DynamicAuthCheckButton = dynamic(import('./AuthCheckButton'), {
  ssr: false,
})
const DynamicCatalogSaveButton = dynamic(import('./CatalogSaveButton'), {
  ssr: false,
})
const DynamicContactButton = dynamic(import('./ContactButton'), { ssr: false })
const DynamicDownloadButton = dynamic(import('./DownloadButton'), {
  ssr: false,
})
const DynamicMailtoButton = dynamic(import('./Mailto'), { ssr: false })

type Props = {
  catalog: CatalogFragment
  thumbnails: { url: string }[] | undefined
}

export const CatalogView = (props: Props) => (
  <StyledCard className={classes.container} elevation={0}>
    <Grid container={true}>
      <Grid item={true} xs={12} sm={5}>
        <CardContent>
          <ImageCarousel thumbnails={props.thumbnails} />
        </CardContent>
      </Grid>
      <Grid item={true} xs={12} sm={7}>
        <CardContent>
          <Typography variant="h5">{props.catalog?.title}</Typography>
          <Link href={`/company/${props.catalog?.company.companyId}/catalog`}>
            <a>
              <Typography variant="subtitle1">{props.catalog?.company.name}</Typography>
            </a>
          </Link>
          <div>
            <React.Suspense fallback={<Skeleton variant="rectangular" height={42} />}>
              <DynamicAuthCheckButton
                buttonText="ダウンロード"
                color="primary"
                variant="contained"
                isCloud={true}
              >
                <DynamicDownloadButton catalog={props.catalog} />
              </DynamicAuthCheckButton>
            </React.Suspense>
            <Grid
              className={classes.containerTop}
              container={true}
              direction="row"
              justifyContent="space-around"
              spacing={1}
            >
              <Grid item xs={12}>
                <React.Suspense fallback={<Skeleton variant="rectangular" height={42} />}>
                  <DynamicAuthCheckButton
                    buttonText="お問い合わせ (見積り・サンプル相談もこちら)"
                    color="grey"
                    variant="outlined"
                  >
                    <DynamicContactButton catalog={props.catalog} />
                  </DynamicAuthCheckButton>
                </React.Suspense>
              </Grid>
              <Grid item={true} xs={6}>
                <React.Suspense fallback={<Skeleton variant="rectangular" height={42} />}>
                  <DynamicAuthCheckButton buttonText="保存" color="grey" variant="outlined">
                    <DynamicCatalogSaveButton catalog={props.catalog} />
                  </DynamicAuthCheckButton>
                </React.Suspense>
              </Grid>
              <Grid item xs={6}>
                <React.Suspense fallback={<Skeleton variant="rectangular" height={42} />}>
                  <DynamicAuthCheckButton buttonText="メールで共有" color="grey" variant="outlined">
                    <DynamicMailtoButton catalog={props.catalog} />
                  </DynamicAuthCheckButton>
                </React.Suspense>
              </Grid>
            </Grid>
            <div className={classes.containerTop}>
              <Typography className={classes.description} variant="h6">
                {props.catalog?.salesPoint}
              </Typography>
            </div>
            <div className={classes.containerTop}>
              <Typography className={classes.description}>{props.catalog?.description}</Typography>
            </div>
          </div>
        </CardContent>
      </Grid>
    </Grid>
  </StyledCard>
)
